// Wizard submission.
//
// Builds the canonical `Submission` payload from the in-memory `WizardState`
// and POSTs it to `/api/public/wizard-submit`. On success the caller offers
// the `portalUrl` from the response — since 2026-08-12 that is the plain
// customer-portal login page, not a magic link: the server emails a temporary
// password instead. On failure we return a structured error so the UI can
// show a retry + "call us" fallback.
//
// The endpoint is being built in parallel; this client must match the agreed
// contract exactly (see master spec / inventory).

import type { PhotoUpload, Submission, SubmissionError, SubmissionSuccess, WizardState } from "../types";
import { renderRoomSvg } from "./canvas";

const ENDPOINT = "/api/public/wizard-submit";

/** Map the wizard's time-of-day choice to a concrete anchor time ("HH:MM").
 *  Anchor = start of the stated range; "Any time" (or anything unknown) stays
 *  empty so the retailer's side treats it as no fixed slot (all-day, retailer
 *  picks the exact time). Keeps the retailer calendar's `/^\d{1,2}:\d{2}/`
 *  contract (see kitchenos-api modules/portal_appointments.js accept flow). */
function anchorPreferredTime(choice: string): string {
  switch (String(choice || "").trim().toLowerCase()) {
    case "morning":   return "09:00"; // Morning (9am–12pm)
    case "afternoon": return "12:00"; // Afternoon (12pm–5pm)
    case "evening":   return "17:00"; // Evening (5pm–7pm)
    default:          return "";      // "Any time" / unknown → no fixed slot
  }
}

export async function submitWizard(
  state: WizardState,
  turnstileToken: string,
): Promise<SubmissionSuccess | SubmissionError> {
  const body = buildPayload(state, turnstileToken);

  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      const errMsg = typeof errBody?.error === "string"
        ? errBody.error
        : `Submission failed (HTTP ${res.status}). Please try again.`;
      return { ok: false, error: errMsg };
    }

    const result = (await res.json()) as Partial<SubmissionSuccess>;
    if (!result || result.ok !== true) {
      return { ok: false, error: "Submission failed — please try again." };
    }
    // `portalUrl` is the plain login page since stage 4 moved to the
    // temp-password email (2026-08-12) — no token, and nothing that makes the
    // submission worth rejecting if it's absent. The submission is saved by
    // this point; failing here would tell the customer it hadn't been.

    return {
      ok: true,
      customerId: String(result.customerId || ""),
      leadId: String(result.leadId || ""),
      portalToken: String(result.portalToken || ""),
      portalUrl: String(result.portalUrl || ""),
    };
  } catch {
    return { ok: false, error: "Network error — please check your connection and try again." };
  }
}

/** Rename the customer's step-1 uploads to `[CustomerName]_plan` /
 *  `[CustomerName]_photo`. Images become `_photo`, everything else (PDF, CAD,
 *  etc.) becomes `_plan`. Multiple files of the same kind get a numeric suffix.
 *  The original extension is preserved. */
function renamePlanUploads(state: WizardState): PhotoUpload[] {
  const uploads = Array.isArray(state.project.planUploads)
    ? state.project.planUploads
    : [];
  if (uploads.length === 0) return [];

  const rawName =
    [state.customer.firstName, state.customer.lastName]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(" ") || "Customer";
  // Filesystem-safe: letters/numbers/spaces only, spaces → underscores.
  const base = rawName.replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_") || "Customer";

  const counts: Record<string, number> = { plan: 0, photo: 0 };
  return uploads.map((f) => {
    const isImage = /^data:image\//i.test(f.dataUrl);
    const kind = isImage ? "photo" : "plan";
    counts[kind] += 1;
    const n = counts[kind]!;
    const ext = extensionFor(f);
    const suffix = n > 1 ? `_${n}` : "";
    return { ...f, name: `${base}_${kind}${suffix}${ext}` };
  });
}

/** Derive a file extension (incl. leading dot) from a data URL mime type,
 *  falling back to the original filename's extension. "" when unknown. */
function extensionFor(f: PhotoUpload): string {
  const MIME_EXT: Record<string, string> = {
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/webp": ".webp",
    "image/gif": ".gif",
    "image/heic": ".heic",
    "application/pdf": ".pdf",
  };
  const m = /^data:([^;,]+)[;,]/i.exec(f.dataUrl || "");
  const mime = m ? m[1]!.toLowerCase() : "";
  if (MIME_EXT[mime]) return MIME_EXT[mime]!;
  const dot = String(f.name || "").lastIndexOf(".");
  if (dot > 0) return f.name.slice(dot).toLowerCase().slice(0, 8);
  return "";
}

/** Recombine the wizard's shape+finish handle answer into the single legacy
 *  `handle_style` token the rest of the platform is keyed on.
 *
 *  This exists because the consumers are LOOKUP MAPS, not free text, and one of
 *  them fails unsafely. `ai_render.js` does:
 *
 *      HANDLE_DESCRIPTORS[s.handle_style] || HANDLE_DESCRIPTORS.chrome_bar
 *
 *  so an unrecognised token does not degrade to "no handle information" — it
 *  silently renders CHROME. Sending a raw "bar" for a customer who asked for
 *  brass would therefore show them the wrong kitchen. Hence the recombination
 *  happens here, on the way out, and `StyleChoices.handleStyle` stays shape-only
 *  inside the wizard.
 *
 *  Deliberately NOT inventing a finish: "bar" with no finish chosen stays
 *  "bar". That token is unknown to the maps and falls back to chrome in the
 *  render path — which is exactly what sending nothing already does today, so
 *  it is no worse, and guessing a metal the customer never picked would be.
 *
 *  Cup and knob keep their bare legacy tokens because the maps have no
 *  finished variants for them yet; the finish still travels in `handle_finish`
 *  for anything that learns to read it. Extending HANDLE_TAG /
 *  HANDLE_DESCRIPTORS to cover cup and knob in each metal is a kitchenos-api
 *  change, flagged separately — nothing here depends on it landing. */
const LEGACY_BAR_BY_FINISH: Record<string, string> = {
  brass:  "brass_bar",
  chrome: "chrome_bar",
  black:  "black_bar",
  // "Steel" is this year's friendlier label for the finish the render engine
  // has always called nickel. Same metal, same descriptor — a label change,
  // not a new option, so it must map onto the existing token.
  steel:  "nickel_bar",
};
export function legacyHandleToken(style: string | undefined, finish: string | undefined): string {
  const s = (style || "").trim();
  const f = (finish || "").trim();
  if (!s) return "";
  if (s === "bar") return LEGACY_BAR_BY_FINISH[f] || "bar";
  return s; // cup | knob | handleless — already legacy tokens
}

/** Construct the submission payload, with the honeypot field forced empty. */
export function buildPayload(state: WizardState, turnstileToken: string): Submission {
  const handleToken = legacyHandleToken(state.style.handleStyle, state.style.handleFinish);
  return {
    retailerId: state.retailerId,
    customer: {
      firstName: state.customer.firstName.trim(),
      lastName:  state.customer.lastName.trim(),
      email:     state.customer.email.trim(),
      phone:     state.customer.phone.trim(),
      address:   state.customer.address.trim(),
      // UK postcode — uppercase + collapse internal whitespace to a single space.
      postcode:  state.customer.postcode.trim().toUpperCase().replace(/\s+/g, " "),
    },
    project: {
      roomDimensions: {
        walls:           state.room.walls,
        doors:           state.room.doors,
        windows:         state.room.windows,
        boilers:         state.room.boilers,
        ceilingHeightMm: state.room.ceilingHeightMm,
      },
      ...(state.room.layoutType ? { layout_type: state.room.layoutType } : {}),
      ...(typeof state.room.hasIsland === "boolean" ? { has_island: state.room.hasIsland } : {}),
      ...(state.style.worktopMaterial ? { worktop_material: state.style.worktopMaterial } : {}),
      ...(handleToken ? { handle_style: handleToken } : {}),
      // Additive: the finish on its own, so the split survives the round-trip
      // even where the legacy token can't carry it (cup/knob). Nothing reads
      // this server-side yet — see the CRM follow-up.
      ...(state.style.handleFinish ? { handle_finish: state.style.handleFinish } : {}),
      ...(state.style.flooringType ? { flooring_type: state.style.flooringType } : {}),
      ...(state.style.doorStyle ? { door_style: state.style.doorStyle } : {}),
      styleChoices: {
        doorRangeId: state.style.doorRangeId,
        doorRange:   state.style.doorRange,
        colourName:  state.style.colourName,
        worktop:     state.style.worktop,
        storage:     state.style.storage,
        appliances:  state.style.appliances,
      },
      notes:      state.room.notes.trim(),
      budget:     state.project.budget,
      timeline:   state.project.timeline,
      leadSource: state.project.leadSource,
      photos:     state.project.photos,
      // Customer-uploaded floor plan / photos, renamed to the agreed
      // [CustomerName]_plan / [CustomerName]_photo convention now the name is
      // known (it's captured on step 3, after the step-1 upload).
      planUploads: renamePlanUploads(state),
      step1Skipped: state.project.step1Skipped === true,
      // Visual floor plan (Bug 12): a self-contained SVG the server saves as a
      // shared design file so it reaches the CRM lead + customer portal docs.
      floorPlanSvg: renderRoomSvg(state.room),
    },
    booking: {
      bookingType:    state.booking.bookingType,
      preferredDate:  state.booking.preferredDate,
      // The wizard offers friendly time-of-day ranges (Morning / Afternoon /
      // Evening), but the retailer's calendar/appointment system expects a
      // concrete "HH:MM" slot — an unrecognised word lands as an all-day event
      // and renders "… at morning". Anchor the range to a real start time on
      // submission so the booking maps cleanly. The customer still sees the
      // friendly range in the wizard; only the submitted value is anchored.
      preferredTime:  anchorPreferredTime(state.booking.preferredTime),
    },
    // Honeypot — legitimate clients always send empty. Bots that fill every
    // visible input tend to fill this too, which lets the backend drop them.
    website: "",
    turnstileToken,
  };
}
